/**
 * Pure payload-assembly for RequestForm.tsx: turns raw HTML-form field
 * values (always strings/booleans from inputs) into the shape
 * validateRequestInput (shared/request-schema.ts) expects. Kept separate
 * from the screen component so it is unit-testable without a DOM shim
 * (test/client/request-form.spec.tsx).
 *
 * This module never validates; it only assembles. RequestForm.tsx feeds
 * its output straight into validateRequestInput so client-side validation
 * is the exact same function the server runs, never a second copy that
 * could drift.
 */
import { pickupInstantFor, type PayerKind } from '../shared/request-schema.js';
import { dublinInstant, dublinWallClock } from '../shared/dublin-time.js';

/**
 * ⛔ `ScheduleMode` IS GONE (T1868 M2), AND SO IS EVERY PATTERN FIELD BELOW.
 * The form expressed "comes back" TWICE — this radio pair, and a separate
 * `returnTrip` checkbox — and LCC asked for one control with two options. The
 * pair now binds to `returnTrip` directly, so there is no mode to carry: every
 * request is a one-off, `journeyDate` is ALWAYS set, and the validator's
 * one-off-XOR-pattern rule is satisfied by construction rather than by branch.
 * Recurrence is refused at the shared validator; see `request-schema.ts`.
 */

export type RequestFormValues = {
  /**
   * ⭐⭐ T2075 M3 (plan D12, R-3) — WHO IS REQUESTING, the form's FIRST control.
   * `requestedByStaffId` is a `portal.staff` id as a string (the picked name's,
   * board 2732 M5: the box's text is `requesterQuery`); `requesterGroup` is the group a reception or admin account narrowed
   * the name list by, and it is NOT part of the payload: the server copies the
   * group snapshot from the STAFF row it re-reads, never from a body, so
   * sending it would only put an unknown key in front of `validateRequestInput`
   * (which drops it silently). A `group` account never sets it.
   */
  requesterGroup: string;
  requestedByStaffId: string;
  /**
   * ⭐⭐ board 2732 M5 (plan DD3) — THE REQUESTER BOX'S TEXT and A STAGED ADD,
   * neither part of the payload (`assembleRequestPayload` copies neither). The
   * text becomes the requester ONLY by a tapped suggestion, a single exact
   * match (`sameStoredName`), or an explicit `Add` tap; any edit clears all
   * three. `requesterAdd` true means "write this name at Send" (`POST
   * /api/staff`), so a form abandoned after the tap writes nothing.
   */
  requesterQuery: string;
  requesterAdd: boolean;
  residentId: string;
  room: string;
  equipment: string[];
  /** A destination id, or the sentinel 'other' when the nurse is typing a
   *  free-text destination instead (P5) — destinationText then carries it. */
  destinationId: string;
  destinationText: string;
  /** T1683 M7 — does the job come back? A plain boolean, from a checkbox.
   *  ⭐ ITS COMPANION TIME LANDED IN T1717 M5: Alex reversed board #1681's
   *  deferral on 2026-09-02 via `/scope` of the QA report (question 708 in the
   *  lane store). `returnTime` below is the one control. ⭐ Board 2638: it is
   *  SENT as typed, and the validator composes it on the journey's Irish day
   *  with the same `dublinInstant` the outbound leg uses, so both legs are
   *  Dublin time and comparable.
   *  ⛔ REVERSED 2026-09-13, Alex, board 2073: the return is SAME-DAY ONLY. The
   *  `returnDate` control T1717 M5 added is gone; a multi-day return is by
   *  phone. */
  returnTrip: boolean;
  /** HH:MM, from <input type="time">; T1717 M5. Blank until the nurse states
   *  it, and only ever read when `returnTrip` is on.
   *  ⭐ BLANK IS AN ANSWER SINCE T2088 M4 (ADR-0006, plan D3): a ticked return
   *  submitted with this at `''` composes no instant and sends
   *  `returnAtState: 'to-confirm'` instead; the row then reads "time to
   *  confirm" and Helen rings. The hint under the control says so. */
  returnTime: string;
  payerKind: string;
  /**
   * The payment-method double-check on the free-typed branch (board 2186, Alex 2026-09-18).
   *
   * ⛔ NOT PART OF THE REQUEST PAYLOAD, for the same reason as `updateRoster` below:
   * `assembleRequestPayload` deliberately does not copy it into the body. D3 says nothing is
   * stored — it exists only to force a pause before a Cash / card job is sent, and the payload
   * key set is pinned exactly at `portal/test/client/request-form.spec.tsx:94`.
   *
   * ⛔ IT LIVES IN `values`, NOT A LOCAL `useState`, because the destination writer must clear it
   * and `payerKind` in the SAME update — a second state cell would let the two drift apart for a
   * render, which is the window where a stale tick could carry across a class change.
   */
  payerConfirmed: boolean;
  /**
   * ⛔⛔ P1-2, AND IT IS NOT PART OF THE REQUEST PAYLOAD. `assembleRequestPayload`
   * below deliberately does NOT copy it into the body: it is an instruction
   * about a SECOND call (`POST /api/residents/:id`), not a field of the request.
   * Sending it would put an unknown key in front of `validateRequestInput`,
   * which ignores it — so the mistake would be silent, which is why the omission
   * is stated here and asserted by the assembly spec's key-set arm.
   */
  updateRoster: boolean;
  /** yyyy-mm-dd, from <input type="date">. ⭐ ALWAYS SET since M2 — there is no
   *  recurring branch that could leave it null. ⭐ Board 2638: SENT AS TYPED, as
   *  the Irish day; the validator composes the instant in `Europe/Dublin`. */
  journeyDate: string;
  /** HH:MM, from <input type="time">. ⚠️ NOT a pattern field — this is the
   *  OUTBOUND time and it survives M2's deletion of the five pattern fields. */
  journeyTime: string;
  /** ⛔ REVERSED 2026-09-13, Alex, board 2073 — the form asks the appointment
   *  time only; the meaning is fixed, not chosen. T632's "never pre-selected"
   *  rule and T1868 M5's radio are withdrawn: there is no control, and this is
   *  the constant `'appointment'` from `emptyRequestFormValues` onward. It is
   *  still SENT, because the validator's REQUIRED arm is untouched and
   *  `collection` stays API-reachable (it derives no collection; board 2638
   *  retired its by-name pickup refusal, now unreachable behind the stale one). */
  timeMeaning: string;
  /** yyyy-mm-dd, written by `derivePickup` from the home's offset; T1868 M5.
   *  No control renders it (2026-09-13, Alex, board 2073); the Collection
   *  preview reads it (board 2188). ⭐ Board 2638: DISPLAY ONLY, the Irish day
   *  of the collection. It is not on the payload: the server derives the stored
   *  collection itself, with the same functions over the same offset column.
   *  ⛔ A DATE AND NOT JUST A TIME,
   *  because the offset runs to 480 minutes: an 07:00 appointment with an
   *  eight-hour offset is picked up at 23:00 the PREVIOUS day, and a time-only
   *  value could not say so. */
  pickupDate: string;
  /** HH:MM, the Irish clock of the collection, written by `derivePickup`;
   *  T1868 M5. Display only, as above. */
  pickupTime: string;
  /**
   * ⛔⛔ THE AUTHORSHIP FLAG, AND IT IS NOT PART OF THE REQUEST PAYLOAD (T1868
   * M5). `assembleRequestPayload` deliberately does not copy it: it records WHO
   * wrote the two controls above — the form's derivation, or the nurse — and
   * that is a fact about this form instance, not a field of the request.
   *
   * ⛔ FALSE MEANS "still the form's value", so a later appointment change may
   * RE-DERIVE it. True means the nurse has touched it, and from that moment the
   * value is hers: nothing recomputes it, and an appointment change that
   * invalidates it WARNS rather than clobbers. It is set at EDIT TIME and never
   * inferred by comparing the value to what the derivation would have produced
   * — a nurse who deliberately retypes the offered value has still authored it,
   * and inferring would silently overwrite her the next time.
   * ⛔ IT IS NEVER RESET BY HAND. Only an identity change resets it, through
   * `emptyRequestFormValues` on a fresh form, exactly as the roster prefill's
   * own authorship is handled.
   */
  pickupTouched: boolean;
  notes: string;
};

export function emptyRequestFormValues(): RequestFormValues {
  return {
    // T2075 M3: blank means nobody named. The validator refuses it by key, so a
    // request cannot leave the form without a name (R-3), and the picker's
    // empty first option is that blank, never the first name in the list.
    requesterGroup: '',
    requestedByStaffId: '',
    // board 2732 M5: the box starts blank and remembers nothing (R-15 not built).
    requesterQuery: '',
    requesterAdd: false,
    residentId: '',
    room: '',
    equipment: [],
    destinationId: '',
    destinationText: '',
    // T1683 M7 — unticked is a real answer ("one way"), unlike the time and the
    // time-meaning controls above, where a default would assert something the
    // nurse never said. Every journey either comes back or it does not.
    returnTrip: false,
    // T632's rule, applied to the second leg: blank means blank. A pre-filled
    // return time would be a plausible, unremarkable, WRONG answer that looked
    // deliberate to Helen - and it reaches a booking, so the consequence is a
    // bus sent at an hour nobody stated.
    returnTime: '',
    payerKind: '',
    // ⛔ UNTICKED IS THE ONLY SAFE DEFAULT. A ticked default would make the double-check
    // decorative — the nurse would confirm a payment method she never read (board 2186).
    payerConfirmed: false,
    // ⛔ UNTICKED IS THE ONLY SAFE DEFAULT (P1-2). A ticked default would make
    // every corrected room silently rewrite the standing roster record, which is
    // the exact write-back DD3 forbids.
    updateRoster: false,
    journeyDate: '',
    // T632: both time controls used to pre-fill '09:00'. A nurse who never
    // touched the control submitted a plausible, unremarkable, WRONG time
    // that looked deliberate to Helen. Blank means blank; the nurse states
    // the time or the request does not validate. (The third default, the one
    // that actually reached the database, was inside the composer, since
    // deleted by board 2638.)
    journeyTime: '',
    // ⛔ THE CONSTANT, NOT A DEFAULT (2026-09-13, Alex, board 2073). There is
    // no radio: the form asks the appointment time only, so the meaning is a
    // fact about the form, not an answer the nurse could have left blank.
    timeMeaning: 'appointment',
    // ⛔ BLANK, LIKE EVERY OTHER TIME CONTROL. The pickup is prefilled from the
    // home's OWN STATED OFFSET once there is an appointment to derive it from —
    // which is a fact about the home, not a guess about this journey — so the
    // empty start is what makes the later fill an ANSWER rather than a default.
    pickupDate: '',
    pickupTime: '',
    pickupTouched: false,
    notes: '',
  };
}

/**
 * ⭐⭐ T1868 M5 (P0-4) — THE COLLECTION THE FORM PREVIEWS: the appointment
 * instant minus the home's stated offset, as the Irish day and clock the
 * Collection line shows.
 *
 * ⭐⭐ Board 2638 (plan D1, D4) — THE SAME TWO FUNCTIONS THE SERVER DERIVES WITH,
 * over the same column: `dublinInstant` composes the appointment in
 * `Europe/Dublin`, `pickupInstantFor` subtracts the offset from the INSTANT, and
 * `dublinWallClock` reads the result back as Irish time. So the preview is what
 * will be stored, whatever zone the device is set to.
 * ⛔ THIS REVERSES THE DEVICE-ZONE DESIGN it replaces. It decomposed with the
 * device's LOCAL accessors because `combineDateAndTime` composed in the device's
 * zone, on an inference drawn from Alex's 2026-08-04 "the home chooses the
 * device". Alex, 2026-09-30 (board 2638): "not reliant on their PCs clocks".
 * ⛔ ON THE INSTANT, NEVER ON THE WALL CLOCK: across a clock change the real lead
 * time is exactly the offset and the clocks shown differ by an hour more or less
 * (a 02:30 GMT appointment 120 minutes early is collected at 01:30 IST, the first
 * 01:30). ⚠️ NEVER BRANCH ON AN is-DST FLAG: Europe/Dublin has used NEGATIVE DST
 * since tzdb 2018 (IST is the standard offset, GMT the saving).
 * ⚠️ Nothing recomposes this pair any more (the payload sends no pickup), so the
 * old round-trip residual in the repeated hour is gone; the preview is display.
 *
 * Returns null when there is nothing to derive from: no appointment date, no
 * appointment time, or no positive offset published for this home.
 */
export function derivePickup(
  journeyDate: string,
  journeyTime: string,
  offsetMinutes: number | null,
): { pickupDate: string; pickupTime: string } | null {
  const appointment = dublinInstant(journeyDate, journeyTime);
  if (appointment === null) return null;
  const pickup = pickupInstantFor(appointment, offsetMinutes);
  if (pickup === null) return null;
  const wall = dublinWallClock(pickup);
  return wall === null ? null : { pickupDate: wall.day, pickupTime: wall.time };
}

/** Numeric field from a <select>'s string value. Empty/non-numeric strings
 *  pass through unchanged (as `''`/NaN) rather than being coerced away, so
 *  validateRequestInput's isPositiveInt check still produces its normal
 *  "Select a resident."/"Select a destination." field error instead of the
 *  assembly step silently hiding an unmade choice. */
function numericOrRaw(raw: string): number | string {
  if (raw.trim().length === 0) return raw;
  const n = Number(raw);
  return Number.isFinite(n) ? n : raw;
}

/** Assembles the raw payload handed to validateRequestInput and, on
 *  success, to POST /api/requests. Not itself a validator: an incomplete
 *  or malformed field is passed through so validateRequestInput's own
 *  field-level errors surface normally instead of being swallowed here. */
export function assembleRequestPayload(values: RequestFormValues): Record<string, unknown> {
  // 'other' is the free-text sentinel (P5): destinationId goes null and
  // destinationText carries what the nurse typed; any curated pick keeps the
  // original numericOrRaw(destinationId) / null-destinationText shape.
  const isOtherDestination = values.destinationId === 'other';
  const base: Record<string, unknown> = {
    residentId: numericOrRaw(values.residentId),
    // ⭐ T2075 M3 — the staff id beside the resident's, through the same
    // `numericOrRaw` so a blank pick reaches the validator as `''` and earns
    // the `requestedByStaffId` refusal rather than being coerced away. The
    // group (`values.requesterGroup`) is deliberately NOT here: see the type.
    requestedByStaffId: numericOrRaw(values.requestedByStaffId),
    room: values.room,
    equipment: values.equipment,
    destinationId: isOtherDestination ? null : numericOrRaw(values.destinationId),
    destinationText: isOtherDestination ? values.destinationText : null,
    // T1683 M7 — carried as a real boolean, never as the checkbox's string, so
    // validateRequestInput's type refusal grades the CALLER and not this step.
    returnTrip: values.returnTrip,
    payerKind: values.payerKind as PayerKind,
    timeMeaning: values.timeMeaning,
    notes: values.notes,
  };

  // ⭐ UNCONDITIONAL SINCE M2. This used to be the `one-off` arm of a branch on
  // `scheduleMode`; the recurring arm below it is deleted with the mode itself.
  //
  // ⭐⭐ Board 2638 (plan D1) — THE WIRE CARRIES WHAT SHE TYPED, AND NO INSTANT.
  // The day, the time and the return time go as the controls hold them, Irish
  // wall clock; the validator composes every instant in `Europe/Dublin` and
  // derives the collection from the home's offset. The browser composed them in
  // the DEVICE's zone until 2638, so a UTC laptop in summer saved every time an
  // hour late. ⛔ NO `returnAt` AND NO `pickupAt` KEY, not even `null`: either
  // one carrying a value is how the validator recognises a page loaded before
  // the deploy (`STALE_PAGE_MESSAGE`), and the key-set arm in
  // `test/client/request-form.spec.tsx` pins their absence.
  return {
    ...base,
    journeyDate: values.journeyDate,
    // T632: a blank time gets its own error, keyed to the control.
    journeyTime: values.journeyTime,
    // ⭐⭐ T2088 M4 (ADR-0006, plan D2/D3) — A BLANK RETURN TIME IS AN ANSWER,
    // AND THIS IS WHERE IT IS SAID: Return ticked and the time left blank sends
    // the marker the validator accepts in place of a time; Helen rings to confirm.
    // ⛔ THE KEY IS ABSENT, NEVER `undefined`, WHEN IT DOES NOT APPLY. An own
    // key `{returnAtState: undefined}` beside a real time is REFUSED by the
    // in-process `validateRequestInput` the form calls ("Say the return time,
    // or say it is to be confirmed") yet ACCEPTED by the server after
    // `JSON.stringify` drops it (M2 security review): the spread is what keeps
    // the client and the server reading the same payload.
    // ⛔ KEYED ON BLANKNESS. A text-fallback browser can hand this form a
    // malformed time ('9.30'), and routing THAT into to-confirm would silently
    // discard a time the nurse typed. Only the empty string is "I do not know
    // yet"; anything else stays a time, and the validator refuses it by name.
    // ⛔ `'unset'` IS NEVER SENT: the wire's only legal value is `'to-confirm'`
    // (`NewRequest.returnAtState`), so a present-but-timed return carries no
    // key at all and D2's marker-plus-a-time refusal is unreachable from here.
    ...(values.returnTrip && values.returnTime === '' ? { returnAtState: 'to-confirm' } : {}),
    // ⛔ ON THE JOURNEY DATE (2026-09-13, Alex, board 2073): the return is
    // same-day only, so the one return control is a time and the day is the
    // appointment's. The validator reads it ONLY when Return is ticked, so a
    // time left in the control after an untick is ignored, not refused.
    returnTime: values.returnTime,
    // ⛔ ALWAYS null since M2 — the browser can no longer construct a pattern.
    // The shared validator still REFUSES one by name, because a direct POST can.
    schedulePattern: null,
  };
}
